import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  collection,
  addDoc,
  query,
  orderBy,
  limit,
  getDocs,
  deleteDoc,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { db } from './config';
import type { SearchResult } from '../types';

// User profile interface
export interface UserProfile {
  userId: string;
  email: string | null;
  createdAt: Timestamp;
  lastActive: Timestamp;
  subscription: {
    isSubscribed: boolean;
    plan: 'pro' | 'max' | null;
    productId: string | null;
    checkoutId: string | null;
    customerEmail: string | null;
    status: string | null;
    subscribedAt: Timestamp | null;
    expiresAt: Timestamp | null;
  };
}

// Subscription data interface
export interface SubscriptionData {
  isSubscribed: boolean;
  plan?: 'pro' | 'max';
  productId?: string;
  checkoutId?: string;
  customerEmail?: string;
  status?: string;
  subscribedAt?: Timestamp;
  expiresAt?: Timestamp | null;
}

/**
 * Initialize a new user document in Firestore
 */
export async function initializeUser(userId: string): Promise<void> {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);

    if (!userDoc.exists()) {
      const newUser: Omit<UserProfile, 'userId'> = {
        email: null,
        createdAt: serverTimestamp() as Timestamp,
        lastActive: serverTimestamp() as Timestamp,
        subscription: {
          isSubscribed: false,
          plan: null,
          productId: null,
          checkoutId: null,
          customerEmail: null,
          status: null,
          subscribedAt: null,
          expiresAt: null,
        },
      };

      await setDoc(userRef, newUser);
      console.log('User initialized in Firestore:', userId);
    }
  } catch (error) {
    console.error('Error initializing user:', error);
    throw error;
  }
}

/**
 * Get user profile from Firestore
 */
export async function getUserProfile(userId: string): Promise<UserProfile | null> {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);

    if (userDoc.exists()) {
      return { userId, ...userDoc.data() } as UserProfile;
    }

    return null;
  } catch (error) {
    console.error('Error getting user profile:', error);
    return null;
  }
}

/**
 * Update user profile fields
 */
export async function updateUserProfile(
  userId: string,
  data: Partial<Omit<UserProfile, 'userId' | 'createdAt'>>
): Promise<void> {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      ...data,
      lastActive: serverTimestamp(),
    });
    console.log('User profile updated:', userId);
  } catch (error) {
    console.error('Error updating user profile:', error);
    throw error;
  }
}

/**
 * Update user subscription details
 */
export async function updateSubscription(
  userId: string,
  subscriptionData: SubscriptionData
): Promise<void> {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      subscription: {
        ...subscriptionData,
        subscribedAt: subscriptionData.subscribedAt || serverTimestamp(),
      },
      lastActive: serverTimestamp(),
    });
    console.log('Subscription updated for user:', userId);
  } catch (error) {
    console.error('Error updating subscription:', error);
    throw error;
  }
}

/**
 * Add a search to user's search history subcollection
 */
export async function addSearch(
  userId: string,
  searchData: Omit<SearchResult, 'id'>
): Promise<string> {
  try {
    const searchesRef = collection(db, 'users', userId, 'searches');
    const searchDoc = {
      ...searchData,
      timestamp: serverTimestamp(),
    };

    const docRef = await addDoc(searchesRef, searchDoc);
    console.log('Search added to Firestore:', docRef.id);
    return docRef.id;
  } catch (error) {
    console.error('Error adding search:', error);
    throw error;
  }
}

/**
 * Get user's search history from Firestore
 */
export async function getUserSearches(
  userId: string,
  limitCount: number = 50
): Promise<SearchResult[]> {
  try {
    const searchesRef = collection(db, 'users', userId, 'searches');
    const q = query(searchesRef, orderBy('timestamp', 'desc'), limit(limitCount));
    const querySnapshot = await getDocs(q);

    const searches: SearchResult[] = [];
    querySnapshot.forEach((doc) => {
      searches.push({
        id: doc.id,
        ...doc.data(),
      } as SearchResult);
    });

    return searches;
  } catch (error) {
    console.error('Error getting user searches:', error);
    return [];
  }
}

/**
 * Delete a search from user's history
 */
export async function deleteSearch(userId: string, searchId: string): Promise<void> {
  try {
    const searchRef = doc(db, 'users', userId, 'searches', searchId);
    await deleteDoc(searchRef);
    console.log('Search deleted:', searchId);
  } catch (error) {
    console.error('Error deleting search:', error);
    throw error;
  }
}

/**
 * Update last active timestamp for user
 */
export async function updateLastActive(userId: string): Promise<void> {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      lastActive: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error updating last active:', error);
    // Don't throw - this is a non-critical operation
  }
}
